import { useHitsPerPage, useInstantSearch, usePagination } from 'react-instantsearch';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FolderX, Plus, SearchX } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useAuth0 } from '@auth0/auth0-react';
import { maxSearchHits, maxSearchPageSize, pageSizeOptions, searchPageSizeOptions } from '@/types/pagination';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useGeneratePageTitle } from '@/hooks/data/useGeneratePageTitle';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useFolderChannel } from '@/hooks/websocket/useFolderChannel';
import { useTableStore } from '@/hooks/data/datatable/useTableStore';
import { GalleryLayout } from '@/components/layouts/gallery-layout';
import { useVariantUrls } from '@/hooks/data/assets/useVariantUrls';
import { useIsSearching } from '@/hooks/data/search/useIsSearching';
import { useSearchStore } from '@/hooks/data/search/useSearchStore';
import { useAlbumChannel } from '@/hooks/websocket/useAlbumChannel';
import { usePathData, useTree } from '@/hooks/data/tree/useTree';
import { useAssetList } from '@/hooks/data/assets/useAssetList';
import { useDataTablePagination } from '@/hooks/usePagination';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { useAccount } from '@/hooks/data/account/useAccount';
import { EmptyLayout } from '@/components/empty-layout';
import { DamLayout } from '@/components/layouts/layout';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import { getCorrectPageNumber } from '@/lib/utils';
import { EntityType } from '@/types/entity';
import { type Asset } from '@/types/asset';
import { DAMNodeType } from '@/types/tree';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

export const convertSearchResults = (
  hits: any,
  ratingOverrides: Record<string, number> = {},
  freshUrls: Record<string, string> = {},
): Array<Asset> => {
  if (!hits) {
    return [];
  }

  return hits.map((hit: any) => {
    const override = ratingOverrides[hit.id];
    const thumbnailUrl = freshUrls[hit.id] ?? hit.thumbnailUrl;

    return {
      id: hit.id,
      name: hit.name,
      slug: hit.slug,
      nodeId: hit.node_id,
      createdAt: hit.createdAt,
      updatedAt: hit.updatedAt,
      description: hit.description,
      trashed: hit.trashed,
      fileMetadata: hit.metadata,
      parentFolderId: hit.parentId,
      parentFolderName: hit.parentName,
      width: hit.version.width,
      height: hit.version.height,
      type: hit.version.fileType ?? null,
      rating: typeof override === 'number' ? override : hit.rating,
      variants: {
        'preview-internal': {
          width: 1200,
          height: 1500,
          url: thumbnailUrl,
        },
        'thumbnail-internal': {
          width: 256,
          height: 320,
          url: thumbnailUrl,
        },
      },
    };
  });
};

export const getSearchPaginationState = (pageIndex: number, limit: number) => {
  const pageSize = Math.min(limit, maxSearchPageSize);
  const offset = pageIndex * limit;
  const maxPageIndex = Math.max(0, Math.ceil(maxSearchHits / pageSize) - 1);
  const normalizedPageIndex = Math.min(Math.floor(offset / pageSize), maxPageIndex);

  return {
    offset: normalizedPageIndex * pageSize,
    pageIndex: normalizedPageIndex,
    pageSize,
  };
};

// Typesense maps an empty query to the "*" wildcard, so InstantSearch echoes results.query
// back as "*". Normalize the committed query that way so the gallery doesn't stay stuck in the
// loading skeleton when the empty query resolves.
export const getIsSearchPending = (status: string, committedQuery: string, resultsQuery: string | undefined) => {
  if (status === 'loading' || status === 'stalled') {
    return true;
  }

  const trimmedQuery = committedQuery.trim();
  const normalizedQuery = trimmedQuery === '' ? '*' : trimmedQuery;

  return normalizedQuery !== (resultsQuery ?? '');
};

const TABLE_ENTITY = EntityType.Asset;

export const HomeComponent = () => {
  const setSelectedNode = useTreeStore((state) => state.setSelectedNode);
  const { deselectAllItems, setSelectedAssets } = useGallerySelection();

  // Permanently deleted: a tombstone. There is no asset behind the id any more, so it
  // leaves the selection and the inspector has nothing to render for it.
  const handleRemoteAssetDeleted = useCallback(
    (assetId: string) => {
      setSelectedAssets((prev) => {
        const next = prev.filter((asset) => asset.id !== assetId);
        return next.length === prev.length ? prev : next;
      });
    },
    [setSelectedAssets],
  );

  // Trashed or restored: the asset still exists, so it stays selected - but the selection
  // carries the flag the inspector reads for its trashed banner and read-only fields.
  // Leaving it stale offers edits the server will reject.
  const handleRemoteAssetTrashStateChanged = useCallback(
    (assetId: string, trashed: boolean) => {
      setSelectedAssets((prev) => {
        if (!prev.some((asset) => asset.id === assetId && Boolean(asset.trashed) !== trashed)) {
          return prev;
        }
        return prev.map((asset) => (asset.id === assetId ? { ...asset, trashed } : asset));
      });
    },
    [setSelectedAssets],
  );

  const openModal = useOpenModal();
  const [pageReady, setPageReady] = useState<boolean>(false);

  const tEmptyStates = useTranslations('emptyStates');
  const tCommon = useTranslations('common');
  const tHome = useTranslations('home');
  const { generatePageTitle } = useGeneratePageTitle();
  const { searchQuery: searchPageQuery, setSearchQuery, ratingOverrides, clearRatingOverrides } = useSearchStore();
  const search = useIsSearching();
  const { isAuthenticated } = useAuth0();
  const { tree, isRootNodesLoading, includeSubFolderAssets, setSidebarTreeOpenIds } = useTree();
  const { query } = useRouter();

  const { locateAssetId, scrollToAssetId } = useBrowserStore(
    useShallow((state) => ({
      locateAssetId: state.locateAssetId,
      scrollToAssetId: state.scrollToAssetId,
    })),
  );

  const setScrollToAssetId = useBrowserStore((state) => state.setScrollToAssetId);

  const { pathSlugs, isBrowsePage, isHomePage } = useCurrentPage();

  // `(hasInitialized || locateAssetId) && isBrowsePage` used to stand here, where `hasInitialized`
  // was the ref *object* rather than its `.current` — always truthy, so the whole left side was
  // dead and this only ever meant `isBrowsePage`. Spelled as what it evaluates to; the ref itself
  // is gone now that the reveal effect below no longer gates on it.
  const { folderIds, folderId, albumId, isPathNotFound } = usePathData(pathSlugs ?? [], isBrowsePage);

  // Subscribe to folder/album channels so `.asset.updated` events merge new
  // variants/dimensions/metadata into the list/gallery caches in place.
  // `currentUserId` drops this viewer's own actions. It matters on the queued paths only -
  // "select all" then Delete posts to /batch and finishes in a worker, which has no socket
  // for the backend to exclude, so those events come back to the person who caused them.
  const { profile } = useAccount();

  useFolderChannel(folderId, {
    enabled: Boolean(folderId) && !search,
    currentUserId: profile?.user?.id,
    onAssetDeleted: handleRemoteAssetDeleted,
    onAssetTrashStateChanged: handleRemoteAssetTrashStateChanged,
  });
  useAlbumChannel(albumId, {
    enabled: Boolean(albumId) && !search,
    currentUserId: profile?.user?.id,
    onAssetDeleted: handleRemoteAssetDeleted,
    onAssetTrashStateChanged: handleRemoteAssetTrashStateChanged,
  });

  const { queryString, setShowFilter } = useSearchQuery(TABLE_ENTITY);

  useEffect(() => {
    if (pageReady) {
      setShowFilter(false);
      deselectAllItems();
    }
  }, [setShowFilter, folderId, albumId, isHomePage]);

  useEffect(() => {
    if (isHomePage) {
      setSelectedNode(undefined, undefined);
    }
  }, [isHomePage, setSelectedNode]);

  useEffect(
    () => () => {
      // Deselect node when unmounting
      setSelectedNode(undefined, undefined);
    },
    [setSelectedNode],
  );

  const { offset, limit, pageIndex, sortOrder, sortValue, setQueryParams } = useDataTablePagination(TABLE_ENTITY);
  const setPageIndex = useTableStore((s) => s.setPageIndex);
  const setPageSize = useTableStore((s) => s.setPageSize);
  const searchPagination = getSearchPaginationState(pageIndex, limit);

  const {
    error,
    assets: browseAssets,
    isPending,
    isLoading,
    isFetching,
    isFetched,
    refetch,
  } = useAssetList(
    {
      offset: locateAssetId ? 0 : offset,
      limit,
    },
    folderId,
    albumId,
    {
      order: sortOrder,
      value: sortValue,
    },
    queryString,
    locateAssetId,
    { enabled: isAuthenticated && (Boolean(folderId) || Boolean(albumId)) },
    includeSubFolderAssets,
    true,
  );

  const ancestors = useMemo(() => browseAssets?.ancestors, [browseAssets]);

  // `search` is derived from the URL (see useIsSearching). Keep the committed search
  // query text in sync with the URL whenever search is active (e.g. on deep-link load).
  useEffect(() => {
    if (search) {
      setSearchQuery((query?.query as string) ?? '');
    }
  }, [search, query?.query]);

  // Per-asset rating overrides only apply to search results; clear them on leaving search.
  const wasSearching = useRef(search);
  useEffect(() => {
    if (wasSearching.current && !search) {
      clearRatingOverrides();
    }
    wasSearching.current = search;
  }, [search, clearRatingOverrides]);

  useEffect(() => {
    if (browseAssets?.nodeId && browseAssets?.name) {
      setSelectedNode(folderId ? DAMNodeType.Folder : DAMNodeType.Album, {
        id: browseAssets.nodeId,
        name: browseAssets.name,
      });
    }
  }, [browseAssets]);

  // Search //
  const { nbHits } = usePagination();
  const { refine } = useHitsPerPage({
    items: searchPageSizeOptions.map((pSize) => ({
      label: `${pSize}`,
      value: pSize,
      default: pSize === searchPageSizeOptions[0],
    })),
  });

  const searchQuery = Array.isArray(query.query) ? query.query[0] : (query.query ?? '');

  useEffect(() => {
    if (!search || limit <= maxSearchPageSize) {
      return;
    }

    setPageSize(TABLE_ENTITY, searchPagination.pageSize);
    setPageIndex(TABLE_ENTITY, searchPagination.pageIndex);
    setQueryParams({
      pageIndex: searchPagination.pageIndex + 1,
      pageSize: searchPagination.pageSize,
    });
  }, [limit, search, searchPagination.pageIndex, searchPagination.pageSize, setPageIndex, setPageSize, setQueryParams]);

  const instance = useInstantSearch();
  useEffect(() => {
    instance.setIndexUiState((prevIndexUiState) => {
      const nextIndexUiState = {
        ...prevIndexUiState,
        query: searchQuery,
        page: searchPagination.pageIndex + 1,
        hitsPerPage: searchPagination.pageSize,
      };

      if (prevIndexUiState.refinementList) {
        nextIndexUiState.refinementList = {
          ...prevIndexUiState.refinementList,
        };
      }

      return nextIndexUiState;
    });
  }, [searchQuery, searchPagination.pageIndex, searchPagination.pageSize]);

  // While searching, the InstantSearch instance starts 'idle' with empty results before the
  // request fires. Treat in-flight requests AND results that don't yet match the committed
  // query as loading, so we show the skeleton instead of flashing the empty "No results" state.
  const isSearchPending = getIsSearchPending(instance.status, searchQuery, instance.results?.query);

  const searchHitIds = useMemo(() => (instance?.results?.hits ?? []).map((h: any) => h.id), [instance?.results?.hits]);
  const { variantUrls } = useVariantUrls(searchHitIds);

  const searchAssets = useMemo(
    () => ({
      name: browseAssets?.name,
      slug: browseAssets?.slug ?? '',
      ancestors: browseAssets?.ancestors ?? [],
      nodeId: browseAssets?.nodeId,
      assets: {
        assets: convertSearchResults(instance?.results?.hits, ratingOverrides, variantUrls),
        pagination: {
          count: Math.min(nbHits, maxSearchHits),
          limit: searchPagination.pageSize,
          offset: searchPagination.offset,
        },
      },
    }),
    [
      instance?.results?.hits,
      browseAssets,
      nbHits,
      ratingOverrides,
      searchPagination.offset,
      searchPagination.pageSize,
      variantUrls,
    ],
  );

  const assets = useMemo(() => (search ? searchAssets : browseAssets), [browseAssets, search, searchAssets]);

  useEffect(() => {
    if (!isFetching && isFetched && assets?.assets?.pagination) {
      const pageNumber = getCorrectPageNumber(assets.assets.pagination, setPageReady);
      if (!search) {
        setQueryParams({
          pageIndex: pageNumber + 1,
          pageSize: assets.assets.pagination.limit,
          sortValue,
          sortOrder,
          showSubFolderAssets: includeSubFolderAssets,
          queryString,
        });
        setPageIndex(TABLE_ENTITY, pageNumber);
      }
    }
  }, [assets, search, isFetching]);

  useEffect(() => {
    if ((locateAssetId || scrollToAssetId) && isFetched && !isFetching) {
      const timeoutId = setTimeout(() => {
        const el = document.getElementById(locateAssetId ?? scrollToAssetId ?? '');

        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
          setScrollToAssetId(undefined);
        }
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [isFetched, locateAssetId, assets, isFetching, scrollToAssetId]);

  /*
   * Reveal the browsed path in the sidebar tree.
   *
   * Runs on every path change, not once per mount. A `hasInitialized` ref used to limit it to the
   * first resolve, so any client-side navigation — a breadcrumb, a notification, a gallery
   * double-click into a child folder — moved the route while leaving every ancestor collapsed, and
   * the tree stopped showing where you were. Repeating is safe by construction: it diffs via the
   * `setSidebarTreeOpenIds` updater's `prev` and writes nothing when there is no new id, so it
   * settles in one pass.
   *
   * Ancestors only. The destination itself is deliberately left shut: expanding a node is the
   * chevron's job and nothing else's, so clicking a folder in the tree, a breadcrumb, a
   * notification or a gallery double-click all reveal where you landed without also opening what
   * is inside it. Opening the destination too was tried and reverted — it made every folder click
   * in the sidebar unfold a subtree the user never asked for. An album is a leaf, so it was never
   * opened either way.
   *
   * Gated on `revealedPathRef`, not just the diff against `prev`. `usePathData` returns a new
   * `folderIds` array on every render (it's a fresh `.map()` each call), so this effect actually
   * re-runs on every render regardless of its dependency array — including a render caused by the
   * user collapsing the active folder's own chevron. Without the ref, that re-run always
   * recomputed `idsToOpen` with the current `folderId` in it and immediately reopened the folder
   * the user had just collapsed. The ref tracks the path this effect last revealed and skips the
   * body entirely when the destination hasn't actually changed, so a collapse sticks until the
   * user actually navigates somewhere else.
   *
   * The key deliberately excludes `locateAssetId`: it's not part of the navigation target, and
   * `toggleNodeHandler` (the chevron click itself) resets it to `undefined` as a side effect of
   * collapsing a node. Keying on it would make collapsing a folder that was entered via a
   * notification or "locate asset" link change the key and re-run the body, reopening the very
   * folder the user just collapsed.
   */
  const revealedPathRef = useRef<string | null>(null);

  useEffect(() => {
    const revealKey = JSON.stringify([folderIds, folderId, albumId]);

    if (revealedPathRef.current === revealKey) {
      return;
    }
    revealedPathRef.current = revealKey;

    setSidebarTreeOpenIds((prev) => {
      const newIds = folderIds.filter((idToOpen: string) => !prev[idToOpen]);

      if (newIds.length === 0) {
        return prev;
      }

      const newOpenIds = { ...prev };
      newIds.forEach((id: string) => {
        newOpenIds[id] = true;
      });
      return newOpenIds;
    });
  }, [folderIds, folderId, albumId, setSidebarTreeOpenIds]);

  return (
    <>
      {generatePageTitle([
        search
          ? tHome('pageTitleSearch', { query: searchPageQuery })
          : isHomePage
            ? tCommon('home')
            : (assets?.name ?? tCommon('assets')),
      ])}
      <DamLayout assets={assets?.assets} refetchNode={refetch}>
        <GalleryLayout
          data={assets}
          assetCount={assets?.assets?.pagination?.count}
          onError={search ? instance.error : error}
          // Search results come from Typesense, not the query cache, so the layout's default
          // invalidation would leave InstantSearch on the same failed response. Browse keeps that
          // default. (DM-2311)
          onRetry={search ? () => instance.refresh() : undefined}
          // `isSearchPending` is deliberately not reused here: it also reports pending whenever the
          // committed query and the echoed results query disagree, which a failed search leaves
          // true indefinitely — the spinner would never stop. InstantSearch's own status is the
          // in-flight signal. (DM-2311)
          isRetrying={search ? instance.status === 'loading' || instance.status === 'stalled' : isFetching}
          isLoading={search ? isSearchPending : !isPathNotFound && (isLoading || !pageReady)}
          isPending={search ? isSearchPending : !isPathNotFound && (isPending || !pageReady)}
          enableDnd={!search}
          refine={refine}
          ancestors={ancestors}
          pagination={{
            pageIndex: search ? searchPagination.pageIndex : pageIndex,
            pageSize: search ? searchPagination.pageSize : limit,
          }}
          pageSizeOptions={search ? searchPageSizeOptions : pageSizeOptions}
          tableEntity={TABLE_ENTITY}
          emptyState={
            search ? (
              <EmptyLayout
                icon={SearchX}
                title={tEmptyStates('searchNoResults.title', { query: searchQuery })}
                message={tEmptyStates('searchNoResults.description')}
              />
            ) : isPathNotFound ? (
              <EmptyLayout
                icon={FolderX}
                title={tEmptyStates('pathNotFound.title')}
                message={tEmptyStates('pathNotFound.description')}
              />
            ) : !isRootNodesLoading && tree.length === 0 ? (
              <EmptyLayout
                icon={FolderX}
                title={tEmptyStates('noFolders.title')}
                message={tEmptyStates('noFolders.description')}
                onClick={() => openModal(ModalId.CreateFolder)}
                buttonIcon={Plus}
                buttonText={tEmptyStates('noFolders.buttonLabel')}
              />
            ) : null
          }
        />
      </DamLayout>
    </>
  );
};
